import type { Tag, GetFamilyIfManyTagsParams } from './types';
import { TAG_GROUPING_CONFIG } from './constants';
import { findParentTags, filterExistingFamilyTags } from './filters';

/**
 * Extracts unique parent IDs from a collection of tags
 *
 * @param tags - Array of tags to extract parent IDs from
 * @returns Set of unique parent IDs (excluding null/undefined)
 */
export function extractUniqueParentIds(tags: Tag[]): Set<number> {
  const parentIds = tags.map(tag => tag.parent).filter((parentId): parentId is number => Boolean(parentId));

  return new Set(parentIds);
}

/**
 * Removes duplicate tags based on their ID
 *
 * @param tags - Array of tags that may contain duplicates
 * @returns Array of unique tags
 */
export function deduplicateTagsById(tags: Tag[]): Tag[] {
  return tags.filter((tag, index, self) => index === self.findIndex(t => t.id === tag.id));
}

/**
 * Determines if tag grouping should be applied based on the number of tags
 *
 * @param tagsCount - Number of tags in the subset
 * @returns True if grouping should be applied
 */
export function shouldApplyTagGrouping(tagsCount: number): boolean {
  return tagsCount > TAG_GROUPING_CONFIG.MAX_TAGS_BEFORE_GROUPING;
}

/**
 * Groups individual product tags under their family/parent tags when the collection is large
 *
 * @param params - Configuration object containing tag collections
 * @returns Reduced set of family tags or original subset if small enough
 *
 * @example
 * ```typescript
 * const result = performTagGrouping({
 *   tagsSubset: [
 *     { id: 1, slug: 'mk3s-plus', parent: 10 },
 *     { id: 2, slug: 'mk4', parent: 10 },
 *     // ... 6 more individual product tags
 *   ],
 *   allTags: [
 *     // ... all tags including families
 *     { id: 10, slug: 'fdm-family', parent: null }
 *   ]
 * });
 * ```
 */
export function performTagGrouping(params: GetFamilyIfManyTagsParams): Tag[] {
  const { tagsSubset, allTags } = params;

  // Extract parent IDs from child tags
  const uniqueParentIds = extractUniqueParentIds(tagsSubset);

  // Find the actual parent/family tags
  const parentTags = findParentTags(allTags, uniqueParentIds);

  // Get tags that are already families
  const existingFamilyTags = filterExistingFamilyTags(tagsSubset);

  // Combine and deduplicate
  const combinedTags = [...parentTags, ...existingFamilyTags];
  return deduplicateTagsById(combinedTags);
}
